import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "../../lib/site";
import { WhatsAppIcon } from "./Header";

const faults = [
  {
    id: "nao-pega",
    label: "Carro não dá partida",
    hint: "Clique ao girar a chave ou silêncio total: geralmente motor de arranque, bateria ou automático.",
  },
  {
    id: "luz-bateria",
    label: "Luz da bateria acesa no painel",
    hint: "O alternador pode não estar carregando. Rodar assim descarrega a bateria em poucos quilômetros.",
  },
  {
    id: "morreu-andando",
    label: "Carro morreu andando",
    hint: "Quadro clássico de bomba de combustível, relé de alimentação ou falha no sistema de carga.",
  },
  {
    id: "cheiro-queimado",
    label: "Cheiro de queimado / fumaça",
    hint: "Curto-circuito ativo. Desligue o carro imediatamente — risco real de incêndio no chicote.",
  },
  {
    id: "eletricos",
    label: "Vidros, travas ou faróis falhando",
    hint: "Falha em fusíveis, relés, aterramento ou módulo. Diagnóstico de circuito resolve no local.",
  },
  {
    id: "outro",
    label: "Outro sintoma elétrico",
    hint: "Descreva o sintoma no WhatsApp. Nosso técnico já sai com o material provável a bordo.",
  },
];

export default function Emergency() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="emergencia" data-testid="emergency-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 glow-amber pointer-events-none" />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
        <Reveal className="text-center">
          <p className="font-tech text-xs uppercase tracking-[0.25em] text-[#e8112d] font-semibold mb-4">
            Assistente de emergência
          </p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-slate-100">
            Qual é o sintoma do seu carro?
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-lg mx-auto">
            Selecione abaixo e receba uma pré-análise imediata. O despacho já vai com o diagnóstico preenchido.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="emergency-fault-selector">
            {faults.map((f) => (
              <button
                key={f.id}
                data-testid={`fault-option-${f.id}`}
                onClick={() => setSelected(f)}
                className={`text-left rounded-xl border p-5 transition-all duration-300 ${
                  selected?.id === f.id
                    ? "border-[#e8112d] bg-[#e8112d]/10 shadow-lg shadow-[#e8112d]/10"
                    : "border-white/10 bg-[#111318] hover:border-[#e8112d]/40"
                }`}
              >
                <AlertTriangle
                  className={`w-5 h-5 mb-3 ${selected?.id === f.id ? "text-[#e8112d]" : "text-slate-600"}`}
                />
                <span className="text-sm font-semibold text-slate-200">{f.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          {selected && (
            <motion.div
              key={selected.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="mt-8 card-metal metal-edge-top rounded-2xl border border-[#e8112d]/30 p-7 sm:p-8"
              data-testid="emergency-diagnosis-result"
            >
              <p className="font-tech text-[11px] uppercase tracking-[0.22em] text-[#c7cbd4] mb-3">
                Pré-análise técnica
              </p>
              <p className="text-base sm:text-lg text-slate-200 leading-relaxed">{selected.hint}</p>
              <a
                href={waLink(
                  `EMERGÊNCIA 24H — Sintoma: ${selected.label}. Pré-análise: ${selected.hint} Meu local: `
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="emergency-dispatch-btn"
                className="mt-6 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-black font-bold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.03] hover:shadow-xl hover:shadow-[#25D366]/25"
              >
                <WhatsAppIcon className="w-5 h-5" />
                Despachar técnico com este diagnóstico
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
